import * as THREE from 'three';

/**
 * The floor guide: a line of small arrowheads marching along the way to go,
 * fading in from your feet and out into the place you are going, which is
 * marked with a pulsing ring and a soft beam of light (D: "a clean, animated
 * arrow or dotted path ... fades near the player and near the target, and
 * clearly marks the end point").
 *
 * Drawing only, like the rest of `world/`. It is handed the corners of the way
 * on the floor (`{ x, y, z }`, from `core/route.js`, which keeps the line a
 * body's width off walls and drops and never cuts a corner) and draws them. It
 * reads nothing from the game and nothing reads it.
 *
 *   corners   rounded: each corner is a short arc (`ROUND`), which bows in
 *             less than the room the route's clearance leaves over a body's
 *             width, so a body following the drawn line always fits
 *   arrows    one every `SPACING` along the line, marching toward the target
 *             at `DRIFT` metres a second, pointing the way, a few centimetres
 *             off whatever floor they are over (they climb stairs with it)
 *   fades     in over the first `FADE_IN` from the feet, out over the last
 *             `FADE_OUT` before the target; drawn as far as `REACH`
 *   the end   a ring on the floor at the target, breathing, and a pale beam
 *             of light above it you can see from across a room
 */

const SPACING = 0.95;
const DRIFT = 1.2;
const REACH = 70;
const FADE_IN = 1.0;
const FADE_OUT = 1.4;
const START = 0.45;
const LIFT = 0.035;
/*
 * At most this much of each leg is rounded, metres. A rounded corner bows in
 * by about 0.29 of it at a right angle, and the route keeps 0.31 off walls for
 * a body of 0.28, so 0.15 is all there is to bow into: at 0.6 the drawn line
 * cut the corner of the apron stairs.
 */
const ROUND = 0.15 / 0.29;
const MAX_ARROWS = Math.ceil(REACH / SPACING) + 2;
const GOLD = new THREE.Color(0xf5b83a);

/** A flat arrowhead pointing along +X, lying in the XZ plane. */
function arrowGeometry() {
  const shape = new THREE.Shape();
  shape.moveTo(0.36, 0);
  shape.lineTo(-0.22, 0.3);
  shape.lineTo(-0.08, 0);
  shape.lineTo(-0.22, -0.3);
  shape.closePath();
  const geometry = new THREE.ShapeGeometry(shape);
  geometry.rotateX(-Math.PI / 2);
  return geometry;
}

/** The corners with each one turned into a short arc, as a dense polyline with distances. */
export function roundCorners(points, radius = ROUND) {
  if (!points || points.length < 3) return (points ?? []).map((p) => ({ ...p }));
  const out = [{ ...points[0] }];
  for (let i = 1; i < points.length - 1; i += 1) {
    const a = points[i - 1];
    const b = points[i];
    const c = points[i + 1];
    const inLength = Math.hypot(b.x - a.x, b.z - a.z);
    const outLength = Math.hypot(c.x - b.x, c.z - b.z);
    const r = Math.min(radius, inLength / 2, outLength / 2);
    if (r < 0.05) {
      out.push({ ...b });
      continue;
    }
    const p = { x: b.x + ((a.x - b.x) / inLength) * r, y: b.y + ((a.y - b.y) / inLength) * r, z: b.z + ((a.z - b.z) / inLength) * r };
    const q = { x: b.x + ((c.x - b.x) / outLength) * r, y: b.y + ((c.y - b.y) / outLength) * r, z: b.z + ((c.z - b.z) / outLength) * r };
    // A quadratic curve from p to q pulled toward the corner: within the
    // corner's triangle, so never further out than the corner itself.
    for (let k = 0; k <= 6; k += 1) {
      const t = k / 6;
      const u = 1 - t;
      out.push({
        x: u * u * p.x + 2 * u * t * b.x + t * t * q.x,
        y: u * u * p.y + 2 * u * t * b.y + t * t * q.y,
        z: u * u * p.z + 2 * u * t * b.z + t * t * q.z,
      });
    }
  }
  out.push({ ...points[points.length - 1] });
  return out;
}

export function createRouteView() {
  const group = new THREE.Group();
  group.name = 'route';
  const canDraw = typeof document !== 'undefined';

  // Solid, not additive: additive gold washed out to nothing on pale asphalt
  // and marble. They fade by shrinking in and out, not by going dim.
  const arrowMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.92,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
  });
  const arrows = new THREE.InstancedMesh(arrowGeometry(), arrowMaterial, MAX_ARROWS);
  arrows.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  arrows.frustumCulled = false;
  arrows.renderOrder = 2;
  arrows.count = 0;
  for (let i = 0; i < MAX_ARROWS; i += 1) arrows.setColorAt(i, GOLD);
  group.add(arrows);

  // The end: a ring on the floor and a pale beam over it.
  const ringMaterial = new THREE.MeshBasicMaterial({ color: GOLD, transparent: true, opacity: 0.9, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
  const ring = new THREE.Mesh(new THREE.RingGeometry(0.34, 0.44, 40), ringMaterial);
  ring.rotation.x = -Math.PI / 2;
  ring.renderOrder = 2;
  const beamMaterial = new THREE.MeshBasicMaterial({ color: GOLD, transparent: true, opacity: 0.16, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
  const beam = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.3, 2.4, 20, 1, true), beamMaterial);
  beam.position.y = 1.2;
  const end = new THREE.Group();
  end.add(ring, beam);
  end.visible = false;
  group.add(end);

  /** The rounded line, with the distance along it at each point. */
  let line = [];
  let total = 0;
  let drift = 0;
  let pulse = 0;
  let shown = false;
  let signature = '';
  const scratch = new THREE.Object3D();
  const colour = new THREE.Color();

  function rebuild(points) {
    line = roundCorners(points);
    total = 0;
    line[0].s = 0;
    for (let i = 1; i < line.length; i += 1) {
      total += Math.hypot(line[i].x - line[i - 1].x, line[i].z - line[i - 1].z);
      line[i].s = total;
    }
    const last = line[line.length - 1];
    end.position.set(last.x, last.y + LIFT, last.z);
  }

  /** A point and heading at distance `s` along the line. */
  function at(s) {
    let i = 1;
    while (i < line.length - 1 && line[i].s < s) i += 1;
    const a = line[i - 1];
    const b = line[i];
    const span = b.s - a.s || 1;
    const t = Math.min(1, Math.max(0, (s - a.s) / span));
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t, heading: Math.atan2(b.z - a.z, b.x - a.x) };
  }

  function place() {
    if (!shown || line.length < 2) {
      arrows.count = 0;
      end.visible = false;
      return;
    }
    const reach = Math.min(total, REACH);
    let n = 0;
    // Marching: every arrow slides forward and a new one comes in behind.
    for (let s = START + (drift % SPACING); s < reach - 0.2 && n < MAX_ARROWS; s += SPACING) {
      const p = at(s);
      const fadeIn = Math.min(1, Math.max(0, (s - START) / FADE_IN));
      const fadeOut = Math.min(1, Math.max(0, (total - s) / FADE_OUT));
      const fadeFar = s > reach - 4 ? Math.max(0, (reach - s) / 4) : 1;
      const strength = fadeIn * fadeOut * fadeFar;
      scratch.position.set(p.x, p.y + LIFT, p.z);
      scratch.rotation.set(0, -p.heading, 0);
      const size = strength;
      scratch.scale.set(size, 1, size);
      scratch.updateMatrix();
      arrows.setMatrixAt(n, scratch.matrix);
      arrows.setColorAt(n, colour.copy(GOLD));
      n += 1;
    }
    arrows.count = n;
    arrows.instanceMatrix.needsUpdate = true;
    if (arrows.instanceColor) arrows.instanceColor.needsUpdate = true;
    end.visible = total <= REACH + 0.5;
  }

  return {
    object3D: group,

    /** The corners to draw, or null for none. Rebuilt only when they change. */
    set(points) {
      if (!points || points.length < 2) {
        shown = false;
        signature = '';
        place();
        return;
      }
      const next = points.map((p) => `${p.x.toFixed(2)},${p.z.toFixed(2)}`).join(';');
      shown = true;
      if (next === signature) return;
      signature = next;
      rebuild(points);
      place();
    },

    /** The arrows marching and the end breathing. Real seconds. */
    update(dt) {
      if (!shown) return;
      drift += dt * DRIFT;
      pulse += dt;
      const breath = 0.5 + 0.5 * Math.sin(pulse * 3.2);
      ring.scale.setScalar(0.9 + 0.25 * breath);
      ringMaterial.opacity = 0.55 + 0.4 * breath;
      beamMaterial.opacity = 0.1 + 0.08 * breath;
      if (canDraw) place();
    },

    /** How many arrows are drawn, for a check. */
    get drawn() {
      return shown ? Math.max(arrows.count, line.length > 1 ? 1 : 0) : 0;
    },
    /** Whether the end is marked, and where. */
    get end() {
      return end.visible ? { x: end.position.x, z: end.position.z } : null;
    },

    dispose() {
      arrows.geometry.dispose();
      arrowMaterial.dispose();
      ring.geometry.dispose();
      ringMaterial.dispose();
      beam.geometry.dispose();
      beamMaterial.dispose();
      group.removeFromParent();
    },
  };
}
